<template>
  <div>
    <div class="i-mb-14">
      <settings-section-title id="webhooks" class="i-mb-2"
        >Notifications</settings-section-title
      >
      <p class="i-text-body-2 i-text-neutral-110">
        Manage notifications for Inscribe
      </p>
    </div>
    <div class="settings-page-block i-py-14 i-section-divide">
      <div class="settings-page-block-title">
        <h5 class="i-text-heading-5 i-text-neutral-120 i-mb-0">
          Email Notifications
        </h5>
        <p class="i-text-body-2 i-text-neutral-110">Manage email preferences</p>
      </div>

      <div class="settings-page-block-content">
        <div class="d-flex flex-column">
          <div class="d-flex align-items-center">
            <h6 class="d-inline-block i-mb-0">
              <strong>Send email when document is done processing</strong>
            </h6>
            <div class="i-pill i-text-subheading-3 i-ml-4">
              <span class="outline">Workspace setting</span>
            </div>
          </div>
          <i-radio
            :disabled="!isOrgAdmin"
            :options="processingSettingsOptions"
            :default-value="processingSettings.preference"
            class="i-my-10"
            @option-selected="setProcessingPreference"
          ></i-radio>
          <div class="d-flex i-mt-3 layout-row align-items-center">
            <label class="d-inline-block i-mr-2"> Recipient: </label>
            <i-select
              placeholder="Select a recipient"
              :options="userOptions"
              :default-value="processingSettings.recipient_id"
              :disabled="
                !isOrgAdmin || processingSettings.preference === 'NONE'
              "
              @option-selected="processingNotificationRecipientChanged"
            ></i-select>
          </div>

          <div class="i-mt-14 d-flex align-items-center">
            <h6 class="d-inline-block i-mb-0">
              <strong>Send email when customer approval status changes</strong>
            </h6>
            <div class="i-pill i-text-subheading-3 i-ml-4">
              <span class="outline">Workspace setting</span>
            </div>
          </div>
          <i-radio
            :disabled="!isOrgAdmin"
            :options="approvalChangeSettingsOptions"
            :default-value="approvalChangeSettings.preference"
            @option-selected="setApprovalPreference"
          ></i-radio>

          <div class="d-flex i-mt-11 layout-row align-items-center">
            <label class="d-inline-block i-mr-2"> Recipient: </label>
            <i-select
              placeholder="Select a recipient"
              :options="userOptions"
              :default-value="approvalChangeSettings.recipient_id"
              :disabled="
                !isOrgAdmin || approvalChangeSettings.preference === 'NONE'
              "
              @option-selected="approvalNotificationRecipientChanged"
            ></i-select>
          </div>
        </div>

        <div class="d-flex flex-column"></div>
      </div>
    </div>
    <div class="settings-page-block i-py-14 i-section-divide">
      <div class="settings-page-block-title">
        <h5 class="i-text-heading-5 i-text-neutral-120 i-mb-0">
          Browser Notifications
        </h5>
        <p class="i-text-body-2 i-text-neutral-110">
          Manage browser notifications
        </p>
      </div>

      <div class="settings-page-block-content">
        <h6 class="d-inline-block i-mb-0">
          <strong>
            Send an browser notification when document is done
            processing</strong
          >
        </h6>
        <i-radio
          :options="browserNotificationSettingsOptions"
          :default-value="
            $store.state.currentUser.browser_notifications_enabled.toString()
          "
          @option-selected="setBrowserNotificationPreference"
        ></i-radio>
        <i-button
          v-if="notificationsSupported"
          text="Test Notification"
          type="neutral"
          subtype="outline"
          data-testid="test-notification-button"
          class="i-mt-4"
          @button-click="testNotification"
        ></i-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { iButton, iRadio, iSelect, showToast } from '@inscribe/signal';

import {
  notificationPermissionsIsGranted,
  notificationsSupported,
  showDocumentProcessedNotification,
} from '@/utils/notifications';
import SettingsSectionTitle from './shared/SettingsSectionTitle';

const NotificationTypes = {
  DOCUMENT_PROCESSED: 'DOCUMENT_PROCESSED',
  CUSTOMER_APPROVAL_CHANGE: 'CUSTOMER_APPROVAL_CHANGE',
};

export default {
  components: {
    'i-button': iButton,
    'i-radio': iRadio,
    'i-select': iSelect,
    'settings-section-title': SettingsSectionTitle,
  },
  data() {
    return {
      allUsers: [],
      notificationsSupported,
      processingSettingsOptions: [
        {
          value: 'ALL',
          title: 'Always',
          subtitle: 'When all customer documents are done processing',
        },
        {
          value: 'FRAUDULENT_DOCUMENTS',
          title: 'Fraudulent customers only',
          subtitle: 'When a customer document is marked as fraudulent',
        },
        {
          value: 'NONE',
          title: 'Never',
        },
      ],
      processingSettings: {
        type: NotificationTypes.DOCUMENT_PROCESSED,
        recipient_id: null,
        preference: 'NONE',
      },
      approvalChangeSettingsOptions: [
        {
          value: 'ALL',
          title: 'Always',
          subtitle: 'When a customer is accepted or rejected',
        },
        {
          value: 'REJECTED_CUSTOMERS',
          title: 'Rejected customers only',
          subtitle: 'When a customer is rejected',
        },
        {
          value: 'NONE',
          title: 'Never',
        },
      ],
      approvalChangeSettings: {
        type: NotificationTypes.CUSTOMER_APPROVAL_CHANGE,
        recipient_id: null,
        preference: 'NONE',
      },
      browserNotificationSettingsOptions: [
        {
          value: 'true',
          title: 'Always',
          subtitle: 'When one of my documents finishes processing',
        },
        {
          value: 'false',
          title: 'Never',
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      isOrgAdmin: 'isOrgAdmin',
    }),
    ...mapState(['currentUser']),
    userOptions() {
      const { email, uuid } = this.currentUser;
      return [
        { text: email, value: uuid },
        ...this.allUsers
          .filter((user) => user.id !== uuid)
          .map((user) => ({
            text: user.email,
            value: user.id,
          })),
      ];
    },
  },
  mounted() {
    this.setupData();
  },
  methods: {
    ...mapActions(['getOrganisationUsers']),
    setupData() {
      this.getOrganisationUsers().then((users) => {
        this.allUsers = this.getActiveHumanUsers(users.data?.data);

        const processingSettings = this.getProcessingSettings();
        if (processingSettings) {
          this.processingSettings = processingSettings;
        } else if (this.userOptions.length) {
          this.processingSettings.recipient_id = this.userOptions[0].value;
        }

        const customerApprovalSetting = this.getCustomerApprovalSetting();
        if (customerApprovalSetting) {
          this.approvalChangeSettings = customerApprovalSetting;
        } else if (this.userOptions.length) {
          this.approvalChangeSettings.recipient_id = this.userOptions[0].value;
        }
      });
    },
    getActiveHumanUsers(users) {
      return users.filter(
        (user) =>
          user.registration_confirmed && user.is_active && user.type === 'USER',
      );
    },
    getProcessingSettings() {
      return this.$store.state.currentUser.organisation.notification_preferences.find(
        ({ type }) => type === 'DOCUMENT_PROCESSED',
      );
    },
    getCustomerApprovalSetting() {
      this.$store.state.currentUser.organisation.notification_preferences.find(
        ({ type }) => type === 'CUSTOMER_APPROVAL_CHANGE',
      );
    },
    setProcessingPreference(preference) {
      this.processingSettings.preference = preference;
      this.updateNotificationPreferences();
    },
    processingNotificationRecipientChanged(newRecipient) {
      this.processingSettings.recipient_id = newRecipient;
      this.updateNotificationPreferences();
    },
    setApprovalPreference(preference) {
      this.approvalChangeSettings.preference = preference;
      this.updateNotificationPreferences();
    },
    approvalNotificationRecipientChanged(newRecipient) {
      this.approvalChangeSettings.recipient_id = newRecipient;
      this.updateNotificationPreferences();
    },
    setBrowserNotificationPreference(preference) {
      this.$store.state.currentUser.browser_notifications_enabled =
        preference === 'true';
      this.updateNotificationPreferences();
    },
    updateNotificationPreferences() {
      const browserNotificationsEnabled =
        this.$store.state.currentUser.browser_notifications_enabled;
      if (
        browserNotificationsEnabled &&
        this.notificationsSupported &&
        !notificationPermissionsIsGranted()
      ) {
        this.requestNotificationPermissions();
      }
      this.$store
        .dispatch('updateNotificationPreferences', {
          browserNotifications: browserNotificationsEnabled,
          organisationPreferences: [
            this.processingSettings,
            this.approvalChangeSettings,
          ],
        })
        .catch((error) => {
          showToast({
            text: error?.data?.message,
            isError: true,
          });
        });
    },
    requestNotificationPermissions() {
      return Notification.requestPermission();
    },
    testNotification() {
      if (!notificationPermissionsIsGranted()) {
        this.requestNotificationPermissions().then((permission) => {
          if (permission !== 'granted') {
            showToast({
              text: 'To send a test notification you must first enable browser notifications. Please try again',
              isError: true,
            });
          } else {
            this.testNotification();
          }
        });
      } else {
        // send test notification
        showDocumentProcessedNotification(
          'Test document',
          '#/settings/notifications',
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.customer-approval-status-changed {
  color: inherit;
  font-size: inherit;
  background: #e0ffea;
}
</style>
